/**
 * Infrared heat lamp geometry only; its warmth is supplied by the room lighting rig.
 *
 * Like ceiling-tube, this is authored from its mounting face at y = 0 and grows
 * upward, so the workbench's universal origin checker remains useful for a ceiling
 * fixture. The caller turns it over about X when mounting it above a creep area.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function cylinderBetween(
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
  name: string,
  material: THREE.Material,
): THREE.Mesh {
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 8, 1), material);
  mesh.name = name;
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'heat-lamp';

  // Materials are per factory call, but shared by all parts on the same surface.
  const bracketMaterial = surface('metal', 0x6e7679);
  const dishMaterial = surface('metal', 0xb4bcbd);
  const guardMaterial = surface('metal', 0x8b9497);
  const bulbMaterial = surface('mineral', 0xd8c4a8);
  // This is insulation, not fabric: fabric is Rosalie's monotonic wear surface.
  const cableMaterial = surface('organic', 0x2b2f31);

  const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.014, 0.07), bracketMaterial);
  bracket.name = 'bracket-plate';
  bracket.position.y = 0.007;
  bracket.castShadow = true;
  group.add(bracket);

  for (const x of [-0.055, 0.055]) {
    const ear = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.03, 0.012), bracketMaterial);
    ear.name = x < 0 ? 'bracket-ear-left' : 'bracket-ear-right';
    ear.position.set(x, 0.029, 0);
    ear.castShadow = true;
    group.add(ear);
  }

  group.add(
    cylinderBetween(
      new THREE.Vector3(0, 0.014, -0.06),
      new THREE.Vector3(0, 0.2, -0.06),
      0.011,
      'hanging-arm',
      bracketMaterial,
    ),
    cylinderBetween(
      new THREE.Vector3(0, 0.2, -0.06),
      new THREE.Vector3(0, 0.2, 0),
      0.011,
      'arm-elbow',
      bracketMaterial,
    ),
  );

  const profile = [
    new THREE.Vector2(0.055, 0.2),
    new THREE.Vector2(0.1, 0.245),
    new THREE.Vector2(0.155, 0.31),
    new THREE.Vector2(0.158, 0.31),
    new THREE.Vector2(0.104, 0.246),
    new THREE.Vector2(0.06, 0.2),
  ];
  const dish = new THREE.Mesh(new THREE.LatheGeometry(profile, 24), dishMaterial);
  dish.name = 'reflector-dish';
  dish.castShadow = true;
  group.add(dish);

  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.045, 16, 8), bulbMaterial);
  bulb.name = 'warm-albedo-bulb';
  bulb.position.y = 0.255;
  bulb.castShadow = true;
  group.add(bulb);

  for (const [index, radius, y] of [[0, 0.15, 0.315], [1, 0.115, 0.335], [2, 0.075, 0.35]] as const) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.004, 4, 20), guardMaterial);
    ring.name = `guard-ring-${index + 1}`;
    ring.rotation.x = Math.PI / 2;
    ring.position.y = y;
    ring.castShadow = true;
    group.add(ring);
  }
  group.add(
    cylinderBetween(new THREE.Vector3(-0.15, 0.34, 0), new THREE.Vector3(0.15, 0.34, 0), 0.006, 'guard-wire-x', guardMaterial),
    cylinderBetween(new THREE.Vector3(0, 0.34, -0.15), new THREE.Vector3(0, 0.34, 0.15), 0.006, 'guard-wire-z', guardMaterial),
    cylinderBetween(new THREE.Vector3(-0.015, 0.014, 0.012), new THREE.Vector3(0.012, 0.06, 0.022), 0.006, 'power-cable', cableMaterial),
  );

  return group;
}

export const heatLampAsset: AssetDefinition = {
  id: 'heat-lamp',
  label: 'Infrared heat lamp',
  description: 'Dished industrial infrared lamp fitting; illumination is supplied by the scene.',
  // Measured off the geometry rather than taken from the brief, which
  // over-declared both the height and the length. The dish's 0.158 m outer rim
  // sets width AND length; the tallest thing is the innermost guard ring at
  // 0.354. `footprint` drives the preview's camera framing, so an over-declared
  // one silently frames every acceptance shot too wide and the 64 px silhouette
  // — the gate that fails most often — gets smaller than it should be.
  footprint: { width: 0.32, height: 0.36, length: 0.32 },
  create: build,
};
